import Phaser from 'phaser'
import { setLogoOverlayVisible, getPersistentBudgetState } from './EventCenter'
import { type PlayerBudgetState } from './PlayerController'

const LEVEL_TRANSITION_VIDEO_SCENE_KEY = 'level-transition-video'
const LEVEL_TRANSITION_VIDEO_CACHE_KEY_PREFIX = 'level-transition-video-cache:'
const DEFAULT_TRANSITION_VIDEO_PATH = 'assets/bali/video-transicion-1.mp4'

export const getLevelTransitionVideoCacheKey = (videoPath: string) => `${LEVEL_TRANSITION_VIDEO_CACHE_KEY_PREFIX}${videoPath}`

export default class LevelTransitionVideo extends Phaser.Scene
{
	private nextLevelIndex?: number
	private videoPath = DEFAULT_TRANSITION_VIDEO_PATH
	private videoCacheKey = getLevelTransitionVideoCacheKey(DEFAULT_TRANSITION_VIDEO_PATH)
	private transitionVideo?: Phaser.GameObjects.Video
	private fadeCurtain?: Phaser.GameObjects.Rectangle
	private resizeHandler?: (gameSize: Phaser.Structs.Size) => void
	private budgetState?: PlayerBudgetState
	private targetSceneKey?: string
	private targetSceneData?: object
	private sourceSceneKey?: string
	private videoRevealed = false

	constructor()
	{
		super(LEVEL_TRANSITION_VIDEO_SCENE_KEY)
	}

	init(data: {
		nextLevelIndex?: number
		videoPath?: string
		budgetState?: PlayerBudgetState
		targetSceneKey?: string
		targetSceneData?: object
		sourceSceneKey?: string
	} = {})
	{
		this.nextLevelIndex = typeof data.nextLevelIndex === 'number' ? data.nextLevelIndex : undefined
		this.videoPath = typeof data.videoPath === 'string' && data.videoPath.trim().length > 0
			? data.videoPath.trim()
			: DEFAULT_TRANSITION_VIDEO_PATH
		this.videoCacheKey = getLevelTransitionVideoCacheKey(this.videoPath)
		this.budgetState = data.budgetState
		this.targetSceneKey = typeof data.targetSceneKey === 'string' && data.targetSceneKey.trim().length > 0
			? data.targetSceneKey.trim()
			: undefined
		this.targetSceneData = data.targetSceneData
		this.sourceSceneKey = typeof data.sourceSceneKey === 'string' && data.sourceSceneKey.trim().length > 0
			? data.sourceSceneKey.trim()
			: undefined
		this.videoRevealed = false
	}

	preload()
	{
		if (!this.cache.video.exists(this.videoCacheKey))
		{
			this.load.video(this.videoCacheKey, this.videoPath, 'canplaythrough', false, false)
		}
	}

	create()
	{
		this.sound.stopAll()

		setLogoOverlayVisible(true)

		if (!this.scene.isActive('logo-overlay'))
		{
			this.scene.launch('logo-overlay')
		}
		this.scene.bringToTop('logo-overlay')

		this.fadeCurtain = this.add.rectangle(
			this.scale.width * 0.5,
			this.scale.height * 0.5,
			this.scale.width,
			this.scale.height,
			0x000000,
			0
		)
		this.fadeCurtain.setDepth(5)

		this.transitionVideo = this.add.video(
			this.scale.width * 0.5,
			this.scale.height * 0.5,
			this.videoCacheKey
		)
		this.transitionVideo.setAlpha(0)
		this.transitionVideo.setDepth(10)
		this.fitVideoToViewport()

		this.transitionVideo.on('complete', this.startNextLevel, this)
		this.transitionVideo.on('error', this.startNextLevel, this)
		this.transitionVideo.on('play', this.revealVideo, this)
		this.transitionVideo.on('created', this.revealVideo, this)

		this.transitionVideo.setMute(false)
		this.transitionVideo.setVolume(1)

		this.tweens.add({
			targets: this.fadeCurtain,
			alpha: 1,
			duration: 180,
			ease: 'Sine.easeOut',
			onComplete: () => {
				this.transitionVideo?.play(false)
			}
		})

		this.resizeHandler = () => {
			this.fitVideoToViewport()
		}
		this.scale.on(Phaser.Scale.Events.RESIZE, this.resizeHandler)

		this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
			if (this.resizeHandler)
			{
				this.scale.off(Phaser.Scale.Events.RESIZE, this.resizeHandler)
				this.resizeHandler = undefined
			}
			this.transitionVideo?.off('complete', this.startNextLevel, this)
			this.transitionVideo?.off('error', this.startNextLevel, this)
			this.transitionVideo?.off('play', this.revealVideo, this)
			this.transitionVideo?.off('created', this.revealVideo, this)
			this.transitionVideo?.destroy()
			this.transitionVideo = undefined
			this.fadeCurtain?.destroy()
			this.fadeCurtain = undefined
		})
	}

	private revealVideo()
	{
		if (this.videoRevealed || !this.transitionVideo)
		{
			return
		}

		this.videoRevealed = true
		this.tweens.add({
			targets: this.transitionVideo,
			alpha: 1,
			duration: 240,
			ease: 'Sine.easeOut'
		})

		if (this.fadeCurtain)
		{
			this.tweens.add({
				targets: this.fadeCurtain,
				alpha: 0,
				duration: 260,
				ease: 'Sine.easeInOut'
			})
		}
	}

	private fitVideoToViewport()
	{
		if (!this.transitionVideo)
		{
			return
		}

		this.transitionVideo
			.setPosition(this.scale.width * 0.5, this.scale.height * 0.5)
			.setDisplaySize(this.scale.width, this.scale.height)

		if (this.fadeCurtain)
		{
			this.fadeCurtain
				.setPosition(this.scale.width * 0.5, this.scale.height * 0.5)
				.setSize(this.scale.width, this.scale.height)
		}
	}

	private startNextLevel()
	{
		const sourceSceneKey = this.sourceSceneKey
		if (sourceSceneKey && (this.scene.isActive(sourceSceneKey) || this.scene.isPaused(sourceSceneKey)))
		{
			this.scene.stop(sourceSceneKey)
		}

		if (this.scale.isFullscreen)
		{
			this.scale.stopFullscreen()
		}

		if (this.targetSceneKey)
		{
			this.scene.start(this.targetSceneKey, this.targetSceneData)
			return
		}

		if (typeof this.nextLevelIndex !== 'number')
		{
			this.scene.start('game', {
				levelIndex: 0,
				budgetState: this.budgetState
			})
			return
		}

		this.scene.start('game', {
			levelIndex: this.nextLevelIndex,
			budgetState: this.budgetState ?? (this.nextLevelIndex === 0 ? undefined : getPersistentBudgetState())
		})
	}
}
